/**
 * Session-based metric change tracking for the dashboard.
 *
 * Captures metric values before Settings is opened and computes
 * deltas when the user returns to the dashboard.
 */

import { createLogger } from "./logger";

const metricLogger = createLogger({ prefix: "MetricDeltaTracker" });

export const STORAGE_KEY = "retireez:metric-snapshot";

export interface MetricSnapshot {
  monthlyIncome: number;
  readinessScore: number;
  yearsToRetirement: number;
  savingsRate: number;
  incomeCoverage: number;
}

export type MetricDeltas = {
  [K in keyof MetricSnapshot]: number;
};

/**
 * Store current metric values in sessionStorage before the user
 * opens Settings.
 */
export function captureMetrics(metrics: MetricSnapshot): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(metrics));
  } catch (error) {
    metricLogger.debug("sessionStorage unavailable for metric capture", {
      error,
    });
  }
}

/**
 * Compute deltas between the current metrics and previously captured
 * values. Returns null if no previous snapshot exists or if all deltas
 * are zero (no meaningful change).
 */
export function getDeltas(current: MetricSnapshot): MetricDeltas | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const previous: MetricSnapshot = JSON.parse(raw) as MetricSnapshot;

    const deltas: MetricDeltas = {
      monthlyIncome: current.monthlyIncome - previous.monthlyIncome,
      readinessScore: current.readinessScore - previous.readinessScore,
      yearsToRetirement: current.yearsToRetirement - previous.yearsToRetirement,
      savingsRate: current.savingsRate - previous.savingsRate,
      incomeCoverage: current.incomeCoverage - previous.incomeCoverage,
    };

    // Return null if nothing changed
    const hasChange = Object.values(deltas).some(
      (d) => Math.abs(d) > Number.EPSILON
    );
    if (!hasChange) return null;

    return deltas;
  } catch (error) {
    metricLogger.debug("sessionStorage unavailable for delta read", { error });
    return null;
  }
}

/**
 * Remove stored metrics from sessionStorage after deltas have been
 * displayed (or on cleanup).
 */
export function clearMetrics(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    metricLogger.debug("sessionStorage unavailable for metric clear", {
      error,
    });
  }
}
